'use client'

import { cn } from '@brew/ui/cn'
import type { ReactNode } from 'react'
import { MARKETING_CANVAS_BG } from '@/components/marketing/v2/marketing-layout'
import { TEMPLATE_CONTAINER_CLASSES } from '../lib/grid-classes'
import {
  type TemplatesSurface,
  TemplatesSurfaceProvider,
} from '../lib/templates-surface-context'

interface TemplatesPageShellProps {
  searchBar: ReactNode
  /** The brand combobox pill (or its skeleton) — leads the filter row. */
  brandFilter: ReactNode
  categoryChips: ReactNode
  gridContent: ReactNode
  surface?: TemplatesSurface
  heading?: string
}

/**
 * Layout shell for the templates gallery (both surfaces): sticky search
 * plus one filter row — brand pill first, category pills scrolling beside
 * it — above the grid. The filter slots stream independently (separate
 * Suspense boundaries over separate `'use cache'` loaders), which is why
 * this composes slots instead of fetching.
 */
export function TemplatesPageShell({
  searchBar,
  brandFilter,
  categoryChips,
  gridContent,
  surface = 'app',
  heading = 'Email templates',
}: TemplatesPageShellProps) {
  return (
    <TemplatesSurfaceProvider surface={surface}>
      <div
        data-templates-surface={surface}
        className="relative flex h-full w-full flex-col overflow-hidden bg-transparent"
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
          <div
            className={cn('sticky top-0 z-10 w-full py-2 md:py-3', {
              [MARKETING_CANVAS_BG]: surface === 'public',
              'bg-background': surface !== 'public',
            })}
            style={{ viewTransitionName: 'templates-filters' }}
          >
            <div
              className={cn(
                TEMPLATE_CONTAINER_CLASSES,
                'flex flex-col gap-2 md:gap-3'
              )}
            >
              {/* No visible label; the page keeps its one heading for
                  screen readers and search. */}
              {surface === 'public' && <h1 className="sr-only">{heading}</h1>}
              {searchBar}
              <div className="flex items-center gap-2">
                {brandFilter}
                <div className="min-w-0 flex-1">{categoryChips}</div>
              </div>
            </div>
          </div>
          {gridContent}
        </div>
      </div>
    </TemplatesSurfaceProvider>
  )
}
